import { IEntityInfo, NSARouterMakerResult } from "namirasoft-account-react";
import { ProductUUID } from "namirasoft-api-product";
import { IStorageLocal } from "namirasoft-core";
import { NamirasoftMap } from "namirasoft-site-map";
import { BaseColumnFormatter, BooleanFormatter, DateTimeFormatter, EnumFormatter, ForeColorFormatter, IDFormatter, JsonFormatter, StringFormatter, StringFormatterSizeType } from "namirasoft-site-react";
import { AssignmentInputRow, AssignmentRow, StorageRow, StorageType, StorageUserInputRow, StorageUserRow } from "namirasoft-storage";
import { StorageUserNameFormatter } from "./components/StorageUserNameFormatter";
import { StorageRouterProps } from "./StorageRouterProps";
import { StorageRoutes } from "./StorageRoutes";

export class StorageEntities
{
    static storage(props: NSARouterMakerResult & StorageRouterProps): IEntityInfo<StorageRow, StorageRow>
    {
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_STORAGE,
            name: "storage",
            tables: [{ table: namirasoft.storage.tables.storage, required: false }],
            client: {
                getListURL: () => StorageRoutes.StorageList,
                getNewURL: () => StorageRoutes.StorageNew,
                getViewURL: (id) => StorageRoutes.StorageView(id),
                getCopyURL: (id) => StorageRoutes.StorageCopy(id),
                getEditURL: (id) => StorageRoutes.StorageEdit(id),
                getColumns: () =>
                {
                    return [
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.id, new IDFormatter(namirasoft, null)),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.workspace_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.log_group_id, new IDFormatter(namirasoft, null)),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.name, new StringFormatter(StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.type, new StringFormatter(StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.meta, new JsonFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.status, new ForeColorFormatter(new EnumFormatter(), {
                            InProgress: "gray",
                            Pending: "orange",
                            Ready: "green",
                            Failed: "red"
                        })),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.status_message, new StringFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.ui_url, new StringFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.created_at, new DateTimeFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage.columns.updated_at, new DateTimeFormatter()),
                    ];
                },
                onCreate: async (entity) =>
                {
                    let body = { ...entity, ...entity.meta };
                    delete body.id;
                    delete body.user_id;
                    delete body.type;
                    delete body.meta;
                    delete body.status;
                    delete body.status_message;
                    delete body.ui_url;
                    delete body.storage_field;
                    delete body.storage_category;
                    delete body.storage_tag;
                    delete body.created_at;
                    delete body.updated_at;

                    switch (entity.type)
                    {
                        case StorageType.AWSS3:
                            return await props.servers.storage.aws_s3.Create(body);
                        case StorageType.AzureBlobStorage:
                            return await props.servers.storage.azure_blob_storage_key.Create(body);
                        case StorageType.AzureFiles:
                            return await props.servers.storage.azure_files_key.Create(body);
                        case StorageType.GCPCloudStorage:
                            return await props.servers.storage.gcp_cloud_storage_o_auth2.Create(body);
                        case StorageType.SFTP:
                            return await props.servers.storage.sftp.Create(body);
                        case StorageType.FTP:
                            return await props.servers.storage.ftp.Create(body);
                        case StorageType.HTTP:
                            return await props.servers.storage.http.Create(body);
                        case StorageType.SMB:
                            return await props.servers.storage.smb.Create(body);
                        case StorageType.WebDAV:
                            return await props.servers.storage.web_dav_user_pass.Create(body);
                        case StorageType.S3:
                            return await props.servers.storage.s3.Create(body);
                        case StorageType.GoogleDrive:
                            return await props.servers.storage.google_drive_o_auth.Create(body);
                        case StorageType.GooglePhotos:
                            return await props.servers.storage.google_photos.Create(body);
                        case StorageType.BackBlazeB2:
                            return await props.servers.storage.back_blaze_b2.Create(body);
                        case StorageType.DropboxOAuth:
                            return await props.servers.storage.dropbox_o_auth.Create(body);
                        case StorageType.OneDriveOAuth:
                            return await props.servers.storage.one_drive_o_auth.Create(body);
                        case StorageType.Box:
                            return await props.servers.storage.box.Create(body);
                        case StorageType.Cloudinary:
                            return await props.servers.storage.cloudinary.Create(body);
                        case StorageType.FileLU:
                            return await props.servers.storage.file_lu.Create(body);
                        case StorageType.FileFabric:
                            return await props.servers.storage.file_fabric.Create(body);
                        case StorageType._1Fichier:
                            return await props.servers.storage.fichier.Create(body);
                        case StorageType.CitrixeShareFile:
                            return await props.servers.storage.citrixe_share_file.Create(body);
                        case StorageType.GoFile:
                            return await props.servers.storage.go_file.Create(body);
                        case StorageType.HDFS:
                            return await props.servers.storage.hdfs.Create(body);
                        case StorageType.HiDrive:
                            return await props.servers.storage.hi_drive.Create(body);
                        case StorageType.ICloudDrive:
                            return await props.servers.storage.i_cloud_drive.Create(body);
                        case StorageType.ImageKit:
                            return await props.servers.storage.image_kit.Create(body);
                        case StorageType.InternetArchive:
                            return await props.servers.storage.internet_archive.Create(body);
                        case StorageType.Jottacloud:
                            return await props.servers.storage.jottacloud.Create(body);
                        case StorageType.Koofr:
                            return await props.servers.storage.koofr.Create(body);
                        case StorageType.Linkbox:
                            return await props.servers.storage.linkbox.Create(body);
                        case StorageType.Mailru:
                            return await props.servers.storage.mailru.Create(body);
                        case StorageType.Mega:
                            return await props.servers.storage.mega.Create(body);
                        case StorageType.OpenDrive:
                            return await props.servers.storage.open_drive.Create(body);
                        case StorageType.OracleObjectStorage:
                            return await props.servers.storage.oracle_object_storage.Create(body);
                        case StorageType.PCloud:
                            return await props.servers.storage.p_cloud_o_auth.Create(body);
                        case StorageType.PikPak:
                            return await props.servers.storage.pik_pak.Create(body);
                        case StorageType.Pixeldrain:
                            return await props.servers.storage.pixeldrain.Create(body);
                        case StorageType.PremiumizeMe:
                            return await props.servers.storage.premiumize_me.Create(body);
                        case StorageType.ProtonDrive:
                            return await props.servers.storage.proton_drive.Create(body);
                        case StorageType.Putio:
                            return await props.servers.storage.putio.Create(body);
                        case StorageType.Qingstor:
                            return await props.servers.storage.qingstor.Create(body);
                        case StorageType.Quatrix:
                            return await props.servers.storage.quatrix.Create(body);
                        case StorageType.Seafile:
                            return await props.servers.storage.seafile.Create(body);
                        case StorageType.Sia:
                            return await props.servers.storage.sia.Create(body);
                        case StorageType.Storj:
                            return await props.servers.storage.storj.Create(body);
                        case StorageType.Swift:
                            return await props.servers.storage.swift.Create(body);
                        case StorageType.Ulozto:
                            return await props.servers.storage.ulozto.Create(body);
                        case StorageType.Yandex:
                            return await props.servers.storage.yandex.Create(body);
                        case StorageType.Zoho:
                            return await props.servers.storage.zoho.Create(body);
                        case StorageType.AkamaiNetStorage:
                            return await props.servers.storage.akamai_net_storage.Create(body);
                        default:
                            throw new Error("Wrong storage type");
                    }
                },
                onUpdate: async (id, entity) =>
                {
                    let body = { ...entity, ...entity.meta };
                    delete body.id;
                    delete body.user_id;
                    delete body.type;
                    delete body.meta;
                    delete body.status;
                    delete body.status_message;
                    delete body.ui_url;
                    delete body.storage_field;
                    delete body.storage_category;
                    delete body.storage_tag;
                    delete body.created_at;
                    delete body.updated_at;

                    switch (entity.type)
                    {
                        case StorageType.AWSS3:
                            return await props.servers.storage.aws_s3.Update(id, body);
                        case StorageType.AzureBlobStorage:
                            return await props.servers.storage.azure_blob_storage_key.Update(id, body);
                        case StorageType.AzureFiles:
                            return await props.servers.storage.azure_files_key.Update(id, body);
                        case StorageType.GCPCloudStorage:
                            return await props.servers.storage.gcp_cloud_storage_o_auth2.Update(id, body);
                        case StorageType.SFTP:
                            return await props.servers.storage.sftp.Update(id, body);
                        case StorageType.FTP:
                            return await props.servers.storage.ftp.Update(id, body);
                        case StorageType.HTTP:
                            return await props.servers.storage.http.Update(id, body);
                        case StorageType.SMB:
                            return await props.servers.storage.smb.Update(id, body);
                        case StorageType.WebDAV:
                            return await props.servers.storage.web_dav_user_pass.Update(id, body);
                        case StorageType.S3:
                            return await props.servers.storage.s3.Update(id, body);
                        case StorageType.GoogleDrive:
                            return await props.servers.storage.google_drive_o_auth.Update(id, body);
                        case StorageType.GooglePhotos:
                            return await props.servers.storage.google_photos.Update(id, body);
                        case StorageType.BackBlazeB2:
                            return await props.servers.storage.back_blaze_b2.Update(id, body);
                        case StorageType.DropboxOAuth:
                            return await props.servers.storage.dropbox_o_auth.Update(id, body);
                        case StorageType.OneDriveOAuth:
                            return await props.servers.storage.one_drive_o_auth.Update(id, body);
                        case StorageType.Box:
                            return await props.servers.storage.box.Update(id, body);
                        case StorageType.Cloudinary:
                            return await props.servers.storage.cloudinary.Update(id, body);
                        case StorageType.FileLU:
                            return await props.servers.storage.file_lu.Update(id, body);
                        case StorageType.FileFabric:
                            return await props.servers.storage.file_fabric.Update(id, body);
                        case StorageType._1Fichier:
                            return await props.servers.storage.fichier.Update(id, body);
                        case StorageType.CitrixeShareFile:
                            return await props.servers.storage.citrixe_share_file.Update(id, body);
                        case StorageType.GoFile:
                            return await props.servers.storage.go_file.Update(id, body);
                        case StorageType.HDFS:
                            return await props.servers.storage.hdfs.Update(id, body);
                        case StorageType.HiDrive:
                            return await props.servers.storage.hi_drive.Update(id, body);
                        case StorageType.ICloudDrive:
                            return await props.servers.storage.i_cloud_drive.Update(id, body);
                        case StorageType.ImageKit:
                            return await props.servers.storage.image_kit.Update(id, body);
                        case StorageType.InternetArchive:
                            return await props.servers.storage.internet_archive.Update(id, body);
                        case StorageType.Jottacloud:
                            return await props.servers.storage.jottacloud.Update(id, body);
                        case StorageType.Koofr:
                            return await props.servers.storage.koofr.Update(id, body);
                        case StorageType.Linkbox:
                            return await props.servers.storage.linkbox.Update(id, body);
                        case StorageType.Mailru:
                            return await props.servers.storage.mailru.Update(id, body);
                        case StorageType.Mega:
                            return await props.servers.storage.mega.Update(id, body);
                        case StorageType.OpenDrive:
                            return await props.servers.storage.open_drive.Update(id, body);
                        case StorageType.OracleObjectStorage:
                            return await props.servers.storage.oracle_object_storage.Update(id, body);
                        case StorageType.PCloud:
                            return await props.servers.storage.p_cloud_o_auth.Update(id, body);
                        case StorageType.PikPak:
                            return await props.servers.storage.pik_pak.Update(id, body);
                        case StorageType.Pixeldrain:
                            return await props.servers.storage.pixeldrain.Update(id, body);
                        case StorageType.PremiumizeMe:
                            return await props.servers.storage.premiumize_me.Update(id, body);
                        case StorageType.ProtonDrive:
                            return await props.servers.storage.proton_drive.Update(id, body);
                        case StorageType.Putio:
                            return await props.servers.storage.putio.Update(id, body);
                        case StorageType.Qingstor:
                            return await props.servers.storage.qingstor.Update(id, body);
                        case StorageType.Quatrix:
                            return await props.servers.storage.quatrix.Update(id, body);
                        case StorageType.Seafile:
                            return await props.servers.storage.seafile.Update(id, body);
                        case StorageType.Sia:
                            return await props.servers.storage.sia.Update(id, body);
                        case StorageType.Storj:
                            return await props.servers.storage.storj.Update(id, body);
                        case StorageType.Swift:
                            return await props.servers.storage.swift.Update(id, body);
                        case StorageType.Ulozto:
                            return await props.servers.storage.ulozto.Update(id, body);
                        case StorageType.Yandex:
                            return await props.servers.storage.yandex.Update(id, body);
                        case StorageType.Zoho:
                            return await props.servers.storage.zoho.Update(id, body);
                        case StorageType.AkamaiNetStorage:
                            return await props.servers.storage.akamai_net_storage.Update(id, body);
                        default:
                            throw new Error("Wrong storage type");
                    }
                },
            },
            server: {
                value_list: props.servers.storage.value.List,
                list: props.servers.storage.storage.List,
                get: props.servers.storage.storage.Get,
                delete: props.servers.storage.storage.Delete,
                create: async (entity) =>
                {
                    let body = {
                        workspace_id: entity.workspace_id,
                        log_group_id: entity.log_group_id,
                        name: entity.name,
                        description: entity.description
                    };

                    switch (entity.type)
                    {
                        case StorageType.AWSS3:
                            return await props.servers.storage.aws_s3.Create({
                                ...body,
                                aws_credential_id: entity.meta.aws_credential_id,
                                provider: entity.meta.provider,
                                region: entity.meta.region,
                            });
                        case StorageType.AzureBlobStorage:
                            return await props.servers.storage.azure_blob_storage_key.Create({
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                            });
                        case StorageType.AzureFiles:
                            return await props.servers.storage.azure_files_key.Create({
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                                share_name: entity.meta.share_name,
                            });
                        case StorageType.GCPCloudStorage:
                            return await props.servers.storage.gcp_cloud_storage_o_auth2.Create({
                                ...body,
                                project_number: entity.meta.project_number,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                location: entity.meta.location,
                            });
                        case StorageType.SFTP:
                            return await props.servers.storage.sftp.Create({
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                key_pem: entity.meta.key_pem,
                                key_file: entity.meta.key_file,
                                key_file_pass: entity.meta.key_file_pass,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.FTP:
                            return await props.servers.storage.ftp.Create({
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                                tls: entity.meta.tls,
                                explicit_tls: entity.meta.explicit_tls,
                            });
                        case StorageType.HTTP:
                            return await props.servers.storage.http.Create({
                                ...body,
                                url: entity.meta.url,
                            });
                        case StorageType.SMB:
                            return await props.servers.storage.smb.Create({
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.WebDAV:
                            return await props.servers.storage.web_dav_user_pass.Create({
                                ...body,
                                url: entity.meta.url,
                                vendor: entity.meta.vendor,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.S3:
                            return await props.servers.storage.s3.Create({
                                ...body,
                                provider: entity.meta.provider,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                region: entity.meta.region,
                            });
                        case StorageType.GoogleDrive:
                            return await props.servers.storage.google_drive_o_auth.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.GooglePhotos:
                            return await props.servers.storage.google_photos.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                read_only: entity.meta.read_only,
                            });
                        case StorageType.BackBlazeB2:
                            return await props.servers.storage.back_blaze_b2.Create({
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                                hard_delete: entity.meta.hard_delete,
                            });
                        case StorageType.DropboxOAuth:
                            return await props.servers.storage.dropbox_o_auth.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.OneDriveOAuth:
                            return await props.servers.storage.one_drive_o_auth.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                tenant: entity.meta.tenant,
                            });
                        case StorageType.Box:
                            return await props.servers.storage.box.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.Cloudinary:
                            return await props.servers.storage.cloudinary.Create({
                                ...body,
                                cloud_name: entity.meta.cloud_name,
                                api_key: entity.meta.api_key,
                                api_secret: entity.meta.api_secret,
                            });
                        case StorageType.FileLU:
                            return await props.servers.storage.file_lu.Create({
                                ...body,
                                key: entity.meta.key,
                            });
                        case StorageType.FileFabric:
                            return await props.servers.storage.file_fabric.Create({
                                ...body,
                                url: entity.meta.url,
                                root_folder_id: entity.meta.root_folder_id,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType._1Fichier:
                            return await props.servers.storage.fichier.Create({
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case StorageType.CitrixeShareFile:
                            return await props.servers.storage.citrixe_share_file.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.GoFile:
                            return await props.servers.storage.go_file.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.HDFS:
                            return await props.servers.storage.hdfs.Create({
                                ...body,
                                namenode: entity.meta.namenode,
                                username: entity.meta.username,
                            });
                        case StorageType.HiDrive:
                            return await props.servers.storage.hi_drive.Create({
                                ...body,
                                scope_access: entity.meta.scope_access,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.ICloudDrive:
                            return await props.servers.storage.i_cloud_drive.Create({
                                ...body,
                                apple_id: entity.meta.apple_id,
                                password: entity.meta.password,
                            });
                        case StorageType.ImageKit:
                            return await props.servers.storage.image_kit.Create({
                                ...body,
                                endpoint: entity.meta.endpoint,
                                public_key: entity.meta.public_key,
                                private_key: entity.meta.private_key,
                            });
                        case StorageType.InternetArchive:
                            return await props.servers.storage.internet_archive.Create({
                                ...body,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                item_derive: entity.meta.item_derive,
                            });
                        case StorageType.Jottacloud:
                            return await props.servers.storage.jottacloud.Create({
                                ...body,
                                tokenURL: entity.meta.tokenURL,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.Koofr:
                            return await props.servers.storage.koofr.Create({
                                ...body,
                                provider: entity.meta.provider,
                                endpoint: entity.meta.endpoint,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.Linkbox:
                            return await props.servers.storage.linkbox.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.Mailru:
                            return await props.servers.storage.mailru.Create({
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.Mega:
                            return await props.servers.storage.mega.Create({
                                ...body,
                                _2fa: entity.meta._2fa,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.OpenDrive:
                            return await props.servers.storage.open_drive.Create({
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.OracleObjectStorage:
                            return await props.servers.storage.oracle_object_storage.Create({
                                ...body,
                                provider: entity.meta.provider,
                                namespace: entity.meta.namespace,
                                compartment: entity.meta.compartment,
                                region: entity.meta.region,
                            });
                        case StorageType.PCloud:
                            return await props.servers.storage.p_cloud_o_auth.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.PikPak:
                            return await props.servers.storage.pik_pak.Create({
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.Pixeldrain:
                            return await props.servers.storage.pixeldrain.Create({
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case StorageType.PremiumizeMe:
                            return await props.servers.storage.premiumize_me.Create({
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case StorageType.ProtonDrive:
                            return await props.servers.storage.proton_drive.Create({
                                ...body,
                                _2fa: entity.meta._2fa,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.Putio:
                            return await props.servers.storage.putio.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.Qingstor:
                            return await props.servers.storage.qingstor.Create({
                                ...body,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                endpoint: entity.meta.endpoint,
                                zone: entity.meta.zone,
                            });
                        case StorageType.Quatrix:
                            return await props.servers.storage.quatrix.Create({
                                ...body,
                                api_key: entity.meta.api_key,
                                host: entity.meta.host,
                            });
                        case StorageType.Seafile:
                            return await props.servers.storage.seafile.Create({
                                ...body,
                                url: entity.meta.url,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                                _2fa: entity.meta._2fa,
                                library: entity.meta.library,
                                library_key: entity.meta.library_key,
                            });
                        case StorageType.Sia:
                            return await props.servers.storage.sia.Create({
                                ...body,
                                api_url: entity.meta.api_url,
                                api_password: entity.meta.api_password,
                            });
                        case StorageType.Storj:
                            return await props.servers.storage.storj.Create({
                                ...body,
                                access_grant: entity.meta.access_grant,
                            });
                        case StorageType.Swift:
                            return await props.servers.storage.swift.Create({
                                ...body,
                                user: entity.meta.user,
                                key: entity.meta.key,
                                auth: entity.meta.auth,
                            });
                        case StorageType.Ulozto:
                            return await props.servers.storage.ulozto.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.Yandex:
                            return await props.servers.storage.yandex.Create({
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.Zoho:
                            return await props.servers.storage.zoho.Create({
                                ...body,
                                region: entity.meta.region,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.AkamaiNetStorage:
                            return await props.servers.storage.akamai_net_storage.Create({
                                ...body,
                                host: entity.meta.host,
                                account: entity.meta.account,
                                secret: entity.meta.secret,
                            });
                        default:
                            throw new Error("Wrong storage type");
                    }
                },
                update: async (id, entity) =>
                {
                    let body = {
                        workspace_id: entity.workspace_id,
                        log_group_id: entity.log_group_id,
                        name: entity.name,
                        description: entity.description
                    };

                    switch (entity.type)
                    {
                        case StorageType.AWSS3:
                            return await props.servers.storage.aws_s3.Update(id, {
                                ...body,
                                aws_credential_id: entity.meta.aws_credential_id,
                                provider: entity.meta.provider,
                                region: entity.meta.region,
                            });
                        case StorageType.AzureBlobStorage:
                            return await props.servers.storage.azure_blob_storage_key.Update(id, {
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                            });
                        case StorageType.AzureFiles:
                            return await props.servers.storage.azure_files_key.Update(id, {
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                                share_name: entity.meta.share_name,
                            });
                        case StorageType.GCPCloudStorage:
                            return await props.servers.storage.gcp_cloud_storage_o_auth2.Update(id, {
                                ...body,
                                project_number: entity.meta.project_number,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                location: entity.meta.location,
                            });
                        case StorageType.SFTP:
                            return await props.servers.storage.sftp.Update(id, {
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                key_pem: entity.meta.key_pem,
                                key_file: entity.meta.key_file,
                                key_file_pass: entity.meta.key_file_pass,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.FTP:
                            return await props.servers.storage.ftp.Update(id, {
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                                tls: entity.meta.tls,
                                explicit_tls: entity.meta.explicit_tls,
                            });
                        case StorageType.HTTP:
                            return await props.servers.storage.http.Update(id, {
                                ...body,
                                url: entity.meta.url,
                            });
                        case StorageType.SMB:
                            return await props.servers.storage.smb.Update(id, {
                                ...body,
                                host: entity.meta.host,
                                port: entity.meta.port,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.WebDAV:
                            return await props.servers.storage.web_dav_user_pass.Update(id, {
                                ...body,
                                url: entity.meta.url,
                                vendor: entity.meta.vendor,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.S3:
                            return await props.servers.storage.s3.Update(id, {
                                ...body,
                                provider: entity.meta.provider,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                region: entity.meta.region,
                            });
                        case StorageType.GoogleDrive:
                            return await props.servers.storage.google_drive_o_auth.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.GooglePhotos:
                            return await props.servers.storage.google_photos.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                read_only: entity.meta.read_only,
                            });
                        case StorageType.BackBlazeB2:
                            return await props.servers.storage.back_blaze_b2.Update(id, {
                                ...body,
                                account: entity.meta.account,
                                key: entity.meta.key,
                                hard_delete: entity.meta.hard_delete,
                            });
                        case StorageType.DropboxOAuth:
                            return await props.servers.storage.dropbox_o_auth.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.OneDriveOAuth:
                            return await props.servers.storage.one_drive_o_auth.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                tenant: entity.meta.tenant,
                            });
                        case StorageType.Box:
                            return await props.servers.storage.box.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.Cloudinary:
                            return await props.servers.storage.cloudinary.Update(id, {
                                ...body,
                                cloud_name: entity.meta.cloud_name,
                                api_key: entity.meta.api_key,
                                api_secret: entity.meta.api_secret,
                            });
                        case StorageType.FileLU:
                            return await props.servers.storage.file_lu.Update(id, {
                                ...body,
                                key: entity.meta.key,
                            });
                        case StorageType.FileFabric:
                            return await props.servers.storage.file_fabric.Update(id, {
                                ...body,
                                url: entity.meta.url,
                                root_folder_id: entity.meta.root_folder_id,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType._1Fichier:
                            return await props.servers.storage.fichier.Update(id, {
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case StorageType.CitrixeShareFile:
                            return await props.servers.storage.citrixe_share_file.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.GoFile:
                            return await props.servers.storage.go_file.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.HDFS:
                            return await props.servers.storage.hdfs.Update(id, {
                                ...body,
                                namenode: entity.meta.namenode,
                                username: entity.meta.username,
                            });
                        case StorageType.HiDrive:
                            return await props.servers.storage.hi_drive.Update(id, {
                                ...body,
                                scope_access: entity.meta.scope_access,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.ICloudDrive:
                            return await props.servers.storage.i_cloud_drive.Update(id, {
                                ...body,
                                apple_id: entity.meta.apple_id,
                                password: entity.meta.password,
                            });
                        case StorageType.ImageKit:
                            return await props.servers.storage.image_kit.Update(id, {
                                ...body,
                                endpoint: entity.meta.endpoint,
                                public_key: entity.meta.public_key,
                                private_key: entity.meta.private_key,
                            });
                        case StorageType.InternetArchive:
                            return await props.servers.storage.internet_archive.Update(id, {
                                ...body,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                item_derive: entity.meta.item_derive,
                            });
                        case StorageType.Jottacloud:
                            return await props.servers.storage.jottacloud.Update(id, {
                                ...body,
                                tokenURL: entity.meta.tokenURL,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.Koofr:
                            return await props.servers.storage.koofr.Update(id, {
                                ...body,
                                provider: entity.meta.provider,
                                endpoint: entity.meta.endpoint,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.Linkbox:
                            return await props.servers.storage.linkbox.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.Mailru:
                            return await props.servers.storage.mailru.Update(id, {
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.Mega:
                            return await props.servers.storage.mega.Update(id, {
                                ...body,
                                _2fa: entity.meta._2fa,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.OpenDrive:
                            return await props.servers.storage.open_drive.Update(id, {
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.OracleObjectStorage:
                            return await props.servers.storage.oracle_object_storage.Update(id, {
                                ...body,
                                provider: entity.meta.provider,
                                namespace: entity.meta.namespace,
                                compartment: entity.meta.compartment,
                                region: entity.meta.region,
                            });
                        case StorageType.PCloud:
                            return await props.servers.storage.p_cloud_o_auth.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.PikPak:
                            return await props.servers.storage.pik_pak.Update(id, {
                                ...body,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.Pixeldrain:
                            return await props.servers.storage.pixeldrain.Update(id, {
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case StorageType.PremiumizeMe:
                            return await props.servers.storage.premiumize_me.Update(id, {
                                ...body,
                                api_key: entity.meta.api_key,
                            });
                        case StorageType.ProtonDrive:
                            return await props.servers.storage.proton_drive.Update(id, {
                                ...body,
                                _2fa: entity.meta._2fa,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.Putio:
                            return await props.servers.storage.putio.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.Qingstor:
                            return await props.servers.storage.qingstor.Update(id, {
                                ...body,
                                access_key_id: entity.meta.access_key_id,
                                secret_access_key: entity.meta.secret_access_key,
                                endpoint: entity.meta.endpoint,
                                zone: entity.meta.zone,
                            });
                        case StorageType.Quatrix:
                            return await props.servers.storage.quatrix.Update(id, {
                                ...body,
                                api_key: entity.meta.api_key,
                                host: entity.meta.host,
                            });
                        case StorageType.Seafile:
                            return await props.servers.storage.seafile.Update(id, {
                                ...body,
                                url: entity.meta.url,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                                _2fa: entity.meta._2fa,
                                library: entity.meta.library,
                                library_key: entity.meta.library_key,
                            });
                        case StorageType.Sia:
                            return await props.servers.storage.sia.Update(id, {
                                ...body,
                                api_url: entity.meta.api_url,
                                api_password: entity.meta.api_password,
                            });
                        case StorageType.Storj:
                            return await props.servers.storage.storj.Update(id, {
                                ...body,
                                access_grant: entity.meta.access_grant,
                            });
                        case StorageType.Swift:
                            return await props.servers.storage.swift.Update(id, {
                                ...body,
                                user: entity.meta.user,
                                key: entity.meta.key,
                                auth: entity.meta.auth,
                            });
                        case StorageType.Ulozto:
                            return await props.servers.storage.ulozto.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                                basic_auth_credential_id: entity.meta.basic_auth_credential_id,
                            });
                        case StorageType.Yandex:
                            return await props.servers.storage.yandex.Update(id, {
                                ...body,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.Zoho:
                            return await props.servers.storage.zoho.Update(id, {
                                ...body,
                                region: entity.meta.region,
                                token_auth_credential_id: entity.meta.token_auth_credential_id,
                            });
                        case StorageType.AkamaiNetStorage:
                            return await props.servers.storage.akamai_net_storage.Update(id, {
                                ...body,
                                host: entity.meta.host,
                                account: entity.meta.account,
                                secret: entity.meta.secret,
                            });
                        default:
                            throw new Error("Wrong storage type");
                    }
                }
            }
        };
    };

    static storage_user(props: NSARouterMakerResult & StorageRouterProps): IEntityInfo<StorageUserRow, StorageUserInputRow>
    {
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_STORAGE,
            name: "storage_user",
            tables: [{ table: namirasoft.storage.tables.storage_user, required: false }],
            client: {
                getListURL: () => StorageRoutes.StorageUserList,
                getNewURL: () => StorageRoutes.StorageUserNew,
                getViewURL: (id) => StorageRoutes.StorageUserView(id),
                getCopyURL: (id) => StorageRoutes.StorageUserCopy(id),
                getEditURL: (id) => StorageRoutes.StorageUserEdit(id),
                getColumns: () =>
                {
                    return [
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage_user.columns.id, new IDFormatter(namirasoft, null)),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage_user.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage_user.columns.name, new StorageUserNameFormatter(StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage_user.columns.password_secret_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage_user.columns.created_at, new DateTimeFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.storage.tables.storage_user.columns.updated_at, new DateTimeFormatter()),
                    ];
                },
            },
            server: {
                value_list: props.servers.storage.value.List,
                list: props.servers.storage.storage_user.List,
                get: props.servers.storage.storage_user.Get,
                delete: props.servers.storage.storage_user.Delete,
                create: async (entity) => await props.servers.storage.storage_user.Create(entity),
                update: async (id, entity) => await props.servers.storage.storage_user.Update(id, entity),
            }
        };
    };

    static assignment(props: NSARouterMakerResult & StorageRouterProps): IEntityInfo<AssignmentRow, AssignmentInputRow>
    {
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_STORAGE,
            name: "assignment",
            tables: [{ table: namirasoft.storage.tables.assignment, required: false }],
            client: {
                getListURL: () => StorageRoutes.AssignmentList,
                getNewURL: () => StorageRoutes.AssignmentNew,
                getViewURL: (id) => StorageRoutes.AssignmentView(id),
                getCopyURL: (id) => StorageRoutes.AssignmentCopy(id),
                getEditURL: (id) => StorageRoutes.AssignmentEdit(id),
                getColumns: () =>
                {
                    let columns = namirasoft.storage.tables.assignment.columns;
                    return [
                        BaseColumnFormatter.getColumn(columns.id, new IDFormatter(namirasoft, null)),
                        BaseColumnFormatter.getColumn(columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(columns.storage_user_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(columns.storage_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(columns.path_source, new StringFormatter(StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(columns.path_destination, new StringFormatter(StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(columns.can_list, new BooleanFormatter("Yes", "No")),
                        BaseColumnFormatter.getColumn(columns.can_download, new BooleanFormatter("Yes", "No")),
                        BaseColumnFormatter.getColumn(columns.can_create_file, new BooleanFormatter("Yes", "No")),
                        BaseColumnFormatter.getColumn(columns.can_create_directory, new BooleanFormatter("Yes", "No")),
                        BaseColumnFormatter.getColumn(columns.can_overwrite_file, new BooleanFormatter("Yes", "No")),
                        BaseColumnFormatter.getColumn(columns.can_delete_file, new BooleanFormatter("Yes", "No")),
                        BaseColumnFormatter.getColumn(columns.can_delete_directory, new BooleanFormatter("Yes", "No")),
                        BaseColumnFormatter.getColumn(columns.can_rename, new BooleanFormatter("Yes", "No")),
                        BaseColumnFormatter.getColumn(columns.can_copy, new BooleanFormatter("Yes", "No")),
                        BaseColumnFormatter.getColumn(columns.can_move, new BooleanFormatter("Yes", "No")),
                        BaseColumnFormatter.getColumn(columns.created_at, new DateTimeFormatter()),
                        BaseColumnFormatter.getColumn(columns.updated_at, new DateTimeFormatter()),
                    ];
                },
            },
            server: {
                value_list: props.servers.storage.value.List,
                list: props.servers.storage.assignment.List,
                get: props.servers.storage.assignment.Get,
                delete: props.servers.storage.assignment.Delete,
                create: async (entity) => await props.servers.storage.assignment.Create(entity),
                update: async (id, entity) => await props.servers.storage.assignment.Update(id, entity),
            }
        };
    };
}